import React, { useCallback, useState, type SyntheticEvent } from 'react';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import Button, { IconButton } from '@atlaskit/button/new';
import ChartTrendIcon from '@atlaskit/icon/core/chart-trend';
import { fg } from '@atlassian/jira-feature-gating';
import type { ArisSprintCommitmentResponseSuccess } from '@atlassian/jira-insights-sprint-commitment/src/common/types.tsx';
import { useIntl, type MessageDescriptor } from '@atlassian/jira-intl';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { EstimationUnit } from '@atlassian/jira-insights-common-constants/src/common/utils/constants.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { timeTrackingFormatter } from '@atlassian/jira-time-tracking-formatter/src/main.tsx';
import { TIME_FORMAT_DEFAULT } from '@atlassian/jira-insights-sprint-commitment/src/common/constants.tsx';
import { fireUIAnalytics, type UIAnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
import { createLocalStorageProvider } from '@atlassian/jira-browser-storage-providers/src/controllers/local-storage/index.tsx';
import { USE_JQL_STORAGE } from '@atlassian/jira-insights-common/src/ui/check-view-aggregated-data-permission/constants.tsx';
import { useSprintCommitmentFetcher } from '../services/index.tsx';
import messages from './messages.tsx';
import { getSprintEstimationTransformer } from './utils.tsx';
import type { SupportedEstimationUnit } from './types.tsx';
import {
	RecommendationIcon,
	RecommendationLabel,
	TooltipMessages,
	TooltipMessagesOld,
} from './constants.tsx';

const useJqlStorage = createLocalStorageProvider(USE_JQL_STORAGE.namespace);

export type Props = {
	rapidViewId: string;
	committed: number;
	estimationType: SupportedEstimationUnit;
	onToggleInsightsPanel: () => void;
};

export const SprintCommitmentIndicator = ({
	rapidViewId,
	committed,
	estimationType,
	onToggleInsightsPanel,
}: Props) => {
	const intl = useIntl();
	const cloudId = useCloudId();
	const [isOpen, setIsOpen] = useState(false);
	const { formatMessage } = useIntl();

	/*
	 * This requires the backlog or board button to render to populate this value via <CheckClassicViewAggregatedDataPermission />.
	 * This can be removed and harcoded to true once insights-use_jql_query_for_enabled_users is cleaned up
	 *
	 * Defaulting this to "true" because this is valid whether user has view aggregated data permission or not.
	 * It may be set to false if user has permission to view aggregated data and the flag is not rolled out.
	 */
	// eslint-disable-next-line react-hooks/react-compiler
	const shouldUseJQLData: boolean = useJqlStorage.get(USE_JQL_STORAGE.key) ?? true;

	const onClick = useCallback(
		(_: SyntheticEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
			setIsOpen(false);
			onToggleInsightsPanel();
			fireUIAnalytics(analyticsEvent, 'toggleInsightsPanel', {
				source: 'sprintCommitmentIndicator',
			});
		},
		[onToggleInsightsPanel],
	);

	const formatter = useCallback(
		(value: number) =>
			estimationType === EstimationUnit.TIME_TRACKING
				? timeTrackingFormatter(value || 0, TIME_FORMAT_DEFAULT, intl)
				: value,
		[estimationType, intl],
	);

	const [{ fetched, loading, data: untypedData, error }, { doFetch }] = useSprintCommitmentFetcher(
		{ getParams: () => ({ cloudId, rapidViewId, shouldUseJQLData }) },
		[cloudId, rapidViewId],
	);

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const data = untypedData as ArisSprintCommitmentResponseSuccess;

	// Note: When an error is present, fetched is NOT set to true.
	if (!fetched && !loading && !data && !error) {
		doFetch({ cloudId, rapidViewId, shouldUseJQLData });
		return null;
	}

	if (loading || !data || error) {
		return null;
	}

	const transform = getSprintEstimationTransformer({ committed, estimationType });
	const { range, recommendationStatus } = transform(data);
	if (recommendationStatus === 'NO_TARGET' || !range) return null;

	const message: MessageDescriptor = (
		fg('jira-issue-terminology-update-m4') ? TooltipMessages : TooltipMessagesOld
	)[estimationType][recommendationStatus];
	const label = RecommendationLabel[recommendationStatus];
	const { Icon, iconColor } = RecommendationIcon[recommendationStatus];

	return (
		<Popup
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			placement="bottom-end"
			messageId="sprint-commitment-indicator.ui.popup"
			messageType="transactional"
			content={() => (
				<Box paddingBlock="space.200" paddingInline="space.300">
					<Stack alignInline="start" space="space.100">
						<Text>
							{intl.formatMessage(message, {
								committed: formatter(committed),
								lowTarget: formatter(range.lower),
								highTarget: formatter(range.upper),
							})}
						</Text>
						<Button iconBefore={ChartTrendIcon} onClick={onClick}>
							{intl.formatMessage(messages.insightsButton)}
						</Button>
					</Stack>
				</Box>
			)}
			trigger={(triggerProps) => (
				<IconButton
					{...triggerProps}
					appearance="subtle"
					icon={(iconProps) => <Icon {...iconProps} color={iconColor} />}
					label={formatMessage(label)}
					onClick={(_, analyticsEvent) => {
						fireUIAnalytics(analyticsEvent, 'sprintCommitmentIndicator');
						setIsOpen(!isOpen);
					}}
					spacing="compact"
				/>
			)}
		/>
	);
};
